// NearMetal sub-domain taxonomy — PRD appendix A, §A3 and §A4.
//
// The three-letter code is not decoration. It is the classification, and it is
// what the part number on every role is built from. If the taxonomy changes,
// the codes change with it: structure is information.

export type DomainCode = 'KRN' | 'CMP' | 'DBS' | 'STO' | 'NET' | 'VRT' | 'EMB' | 'PRF'

export type Domain = {
  code: DomainCode
  /** Stored in `jobs.domains`. Changing this requires a migration. */
  name: string
  /** URL segment for `/jobs/<slug>`. */
  slug: string
  /** Landing-page sentence. States what the work is, never how good it is. */
  blurb: string
  /**
   * The heading a candidate would actually type into a search box. `name` is the
   * internal label and `code` is the filing prefix; neither belongs in an H1,
   * because nobody searches for `KRN · Kernel & Drivers`.
   */
  heading: string
  /**
   * The rest of the `<title>` after the heading: the concrete work a searcher
   * scans the result for. Kept short — heading, qualifier, and brand together
   * should fit the ~60 characters a result shows.
   */
  titleQualifier: string
  /** What the page covers, stated as work rather than keywords. */
  scope: string
  /** What it deliberately excludes, and where those roles are filed instead. */
  excludes: string
  /**
   * Title evidence that files a role here first when the classifier assigned
   * several sub-domains. Only consulted among codes already assigned, so this
   * decides ordering and never adds a classification.
   */
  titlePatterns: readonly RegExp[]
}

export const DOMAINS: readonly Domain[] = [
  {
    code: 'KRN',
    name: 'Kernel & Drivers',
    slug: 'kernel-drivers',
    blurb: 'Roles in kernel subsystems, device drivers, hardware interfaces, and the code that runs before user space does.',
    heading: 'Linux Kernel & Device Driver Jobs',
    titleQualifier: 'Kernel Engineer Roles',
    scope: 'Kernel subsystems, device drivers, board support packages, hardware interfaces, memory management, scheduling, and the code that runs before user space does.',
    excludes: 'Kernel-bypass data planes are filed under networking, hypervisor and container isolation under virtualization, and firmware that never boots a general-purpose kernel under embedded.',
    // Patterns name the *system*, never the job title. A phrase that names a
    // system is stronger evidence than the word inside it, and the longest-match
    // rule prefers it automatically — so `linux kernel` is listed as well as
    // `kernel`. Adding title-shaped patterns like `storage engineer` would give
    // whichever sub-domain happens to own the noun in the job title an
    // artificial edge, which is measurement of the title, not of the work.
    titlePatterns: [/\blinux kernel\b/, /\bkernel internals\b/, /\bkernel\b/, /\bdevice driver/, /\bdriver (?:engineer|developer|development)\b/, /\blinux internals\b/, /\bbsp\b/, /\bboard support\b/, /\boperating system/, /\bsyscall/],
  },
  {
    code: 'CMP',
    name: 'Compilers & Runtimes',
    slug: 'compilers-runtimes',
    blurb: 'Roles in front ends, optimizers, code generation, language runtimes, garbage collectors, and JIT engines.',
    heading: 'Compiler Engineer Jobs',
    titleQualifier: 'LLVM, JIT & Runtimes',
    scope: 'Front ends, optimizers, code generation, LLVM and MLIR work, language runtimes, garbage collectors, JIT engines, and toolchains.',
    excludes: 'Container runtimes are filed under virtualization, and GPU kernel authoring that is measured rather than compiled is filed under performance.',
    titlePatterns: [/\bcompiler/, /\bllvm\b/, /\bmlir\b/, /\bcodegen\b/, /\bcode generation\b/, /\bjit\b/, /\btoolchain/, /\binterpreter\b/, /\bgarbage collect/, /\blanguage (?:runtime|design|engineer)/, /\bwebassembly\b/, /\bwasm\b/],
  },
  {
    code: 'DBS',
    name: 'Databases & Query Engines',
    slug: 'databases-query-engines',
    blurb: 'Roles in query planners, execution engines, transaction and index internals, and distributed consensus.',
    heading: 'Database Engineer Jobs',
    titleQualifier: 'Database Internals',
    scope: 'Query planners and optimizers, vectorized execution, transaction and index internals, write-ahead logging, and distributed consensus.',
    excludes: 'Application backends that use a database are out of scope entirely, and the durability layer underneath an engine is filed under storage.',
    titlePatterns: [/\bdatabase/, /\bquery (?:engine|planner|optimi[sz]|processing|execution)/, /\bsql\b/, /\bolap\b/, /\boltp\b/, /\bpostgres/, /\bdata(?:base)? internals\b/, /\btransaction/, /\bconsensus\b/, /\bdata warehouse/, /\bindexing engine\b/],
  },
  {
    code: 'STO',
    name: 'Storage & Filesystems',
    slug: 'storage-filesystems',
    blurb: 'Roles in block layers, filesystems, object stores, replication, and the durability path underneath them.',
    heading: 'Storage Engineer Jobs',
    titleQualifier: 'Filesystems & Storage Engines',
    scope: 'Block layers, filesystems, object stores, replication and erasure coding, caching tiers, and the durability path underneath a database or an application.',
    excludes: 'Query processing above the storage layer is filed under databases, and NVMe or SSD controller firmware under embedded.',
    titlePatterns: [/\bstorage engine\b/, /\bdistributed storage\b/, /\bstorage\b/, /\bfile ?system/, /\bobject stor/, /\bblock (?:layer|storage|device)/, /\bdurabilit/, /\breplication\b/, /\berasure cod/, /\bs3\b/],
  },
  {
    code: 'NET',
    name: 'Networking & Protocols',
    slug: 'networking-protocols',
    blurb: 'Roles in data planes, protocol stacks, eBPF and kernel bypass, load balancing, and switch and NIC software.',
    heading: 'Network Software Engineer Jobs',
    titleQualifier: 'Protocols & eBPF',
    scope: 'Data planes, protocol stacks, eBPF and kernel bypass, load balancing, congestion control, and switch, NIC, and RDMA software.',
    excludes: 'Network application backends and site reliability work are out of scope; NIC firmware is filed under embedded.',
    titlePatterns: [/\bnetwork/, /\bprotocol\b/, /\bebpf\b/, /\bxdp\b/, /\bdpdk\b/, /\btcp\b/, /\bquic\b/, /\brdma\b/, /\bdata ?plane\b/, /\bload balanc/, /\bpacket/, /\brouting\b/, /\bswitch(?:ing)? software\b/, /\bnic\b/],
  },
  {
    code: 'VRT',
    name: 'Virtualization & Containers',
    slug: 'virtualization-containers',
    blurb: 'Roles in hypervisors, VMMs, sandboxes, container runtimes, and the isolation boundaries they enforce.',
    heading: 'Virtualization Engineer Jobs',
    titleQualifier: 'Hypervisors & Containers',
    scope: 'Hypervisors, virtual machine monitors, sandboxes, container and microVM runtimes, and the isolation boundaries they enforce.',
    excludes: 'Kubernetes platform and cluster operations roles that never touch a runtime are out of scope; device passthrough drivers are filed under kernel.',
    titlePatterns: [/\bvirtuali[sz]ation\b/, /\bhypervisor\b/, /\bvmm\b/, /\bkvm\b/, /\bqemu\b/, /\bxen\b/, /\bcontainer runtime\b/, /\bcontainer\b/, /\bsandbox/, /\bisolation\b/, /\bfirecracker\b/, /\bmicrovm\b/],
  },
  {
    code: 'EMB',
    name: 'Embedded & Firmware',
    slug: 'embedded-firmware',
    blurb: 'Roles in firmware, bootloaders, RTOS work, board bring-up, and software written against a datasheet.',
    heading: 'Embedded Software Engineer Jobs',
    titleQualifier: 'Firmware & RTOS',
    scope: 'Firmware, bootloaders, RTOS work, board bring-up, silicon enablement, and software written against a datasheet rather than an API.',
    excludes: 'Linux device drivers running under a general-purpose kernel are filed under kernel, and hardware design roles that write no software are out of scope.',
    titlePatterns: [/\bembedded\b/, /\bfirmware\b/, /\bbootloader\b/, /\brtos\b/, /\bzephyr\b/, /\bfreertos\b/, /\bbare[- ]metal\b/, /\bmicrocontroller\b/, /\bmcu\b/, /\bsilicon\b/, /\bbring[- ]up\b/, /\bfpga\b/],
  },
  {
    code: 'PRF',
    name: 'Performance & Observability',
    slug: 'performance-observability',
    blurb: 'Roles in profiling, tracing, latency work, GPU and accelerator kernels, and measuring what the machine actually does.',
    heading: 'Performance Engineer Jobs',
    titleQualifier: 'Profiling & Low Latency',
    scope: 'Profiling, tracing, latency and throughput work, GPU and accelerator kernel authoring, compiler-adjacent tuning, and measuring what the machine actually does.',
    excludes: 'Product analytics and application monitoring are out of scope; the tracing subsystem inside a kernel is filed under kernel.',
    titlePatterns: [/\bperformance\b/, /\blow[- ]latency\b/, /\blatency\b/, /\bprofil(?:er|ing)\b/, /\btracing\b/, /\bobservabilit/, /\boptimi[sz]ation\b/, /\bgpu kernel/, /\bcuda\b/, /\btriton\b/, /\bbenchmark/, /\bhpc\b/, /\bhigh[- ]performance\b/],
  },
] as const

export const DOMAIN_BY_CODE = new Map(DOMAINS.map((domain) => [domain.code, domain]))
export const DOMAIN_BY_SLUG = new Map(DOMAINS.map((domain) => [domain.slug, domain]))
const DOMAIN_BY_NAME = new Map(DOMAINS.map((domain) => [domain.name.toLowerCase(), domain]))
const DOMAIN_ORDER = new Map(DOMAINS.map((domain, index) => [domain.code, index]))

/**
 * Pre-refactor domain names and slugs, kept so that stored rows, saved
 * newsletter preferences, and indexed URLs keep resolving. A legacy name may
 * map to more than one code: `Databases & Storage` was two domains in a coat.
 */
const LEGACY_NAMES: Record<string, DomainCode[]> = {
  'operating systems & kernel': ['KRN'],
  'systems security': ['KRN'],
  'browsers & engines': ['CMP'],
  'databases & storage': ['DBS', 'STO'],
  networking: ['NET'],
  'graphics, gpu & compute': ['PRF'],
  'low-latency & performance': ['PRF'],
}

export const LEGACY_SLUG_REDIRECTS: Record<string, string> = {
  'operating-systems-kernel': 'kernel-drivers',
  'systems-security': 'kernel-drivers',
  'browsers-engines': 'compilers-runtimes',
  'databases-storage': 'databases-query-engines',
  networking: 'networking-protocols',
  'graphics-gpu-compute': 'performance-observability',
  'low-latency-performance': 'performance-observability',
}

export function domainFromCode(code: string | null | undefined) {
  return DOMAIN_BY_CODE.get(String(code || '').toUpperCase() as DomainCode) ?? null
}

export function domainFromSlug(slug: string) {
  return DOMAIN_BY_SLUG.get(slug) ?? null
}

/** Resolves a stored domain name — current or legacy — to canonical codes. */
export function codesForDomainName(name: string): DomainCode[] {
  const key = String(name || '').trim().toLowerCase()
  const current = DOMAIN_BY_NAME.get(key)
  if (current) return [current.code]
  return LEGACY_NAMES[key] ?? []
}

/** Canonical, deduplicated, taxonomy-ordered codes for a role. */
export function jobDomainCodes(job: { domains?: string[] | null }): DomainCode[] {
  const codes = new Set<DomainCode>()
  for (const name of job.domains ?? []) for (const code of codesForDomainName(name)) codes.add(code)
  return [...codes].sort((a, b) => (DOMAIN_ORDER.get(a) ?? 99) - (DOMAIN_ORDER.get(b) ?? 99))
}

/**
 * The single code a role is *filed* under.
 *
 * The classifier assigns a role every sub-domain it touches — a little over
 * three each, on average — which is right for the role and wrong for a landing
 * page: a `Training Performance Engineer` tagged kernel, networking, and
 * performance dilutes all three pages and makes them read alike. The title is
 * the strongest available evidence of what the job actually is, so among the
 * codes already assigned, the longest title match leads and the rest become
 * secondary. Taxonomy order breaks a tie, and a role whose title says nothing
 * keeps the previous behaviour.
 *
 * This never invents a classification: a code the classifier did not assign can
 * never win here.
 */
export function primaryDomainCode(job: { domains?: string[] | null; title?: string | null }) {
  const codes = jobDomainCodes(job)
  if (codes.length <= 1) return codes[0] ?? null
  const title = String(job.title ?? '').toLowerCase()
  if (!title) return codes[0]
  let best: { code: DomainCode; weight: number } | null = null
  for (const code of codes) {
    let weight = 0
    for (const pattern of DOMAIN_BY_CODE.get(code)!.titlePatterns) {
      const match = pattern.exec(title)
      if (match && match[0].length > weight) weight = match[0].length
    }
    if (weight && (!best || weight > best.weight)) best = { code, weight }
  }
  return best?.code ?? codes[0]
}

/** Everything else the role touches. Shown as cross-links, never as the filing. */
export function secondaryDomainCodes(job: { domains?: string[] | null; title?: string | null }): DomainCode[] {
  const primary = primaryDomainCode(job)
  return jobDomainCodes(job).filter((code) => code !== primary)
}

export function domainsForJob(job: { domains?: string[] | null }): Domain[] {
  return jobDomainCodes(job).map((code) => DOMAIN_BY_CODE.get(code)!)
}

/**
 * Canonical names for storage. Applied on every write path so a legacy or
 * operator-typed value never lands in the database.
 */
export function normalizeDomainNames(names: readonly string[] | null | undefined): string[] {
  return jobDomainCodes({ domains: [...(names ?? [])] }).map((code) => DOMAIN_BY_CODE.get(code)!.name)
}

/**
 * `KRN·0141`. The sequence is a stable per-role integer; the prefix is the
 * role's current classification and moves if the role is reclassified. Permanent
 * links use `jobs.slug`, never the part number (appendix A §A9.3).
 */
export function formatPartNumber(code: string | null | undefined, seq: number | null | undefined) {
  const number = Number(seq)
  if (!Number.isFinite(number) || number <= 0) return ''
  return `${(code || 'SYS').toUpperCase()}·${String(Math.trunc(number)).padStart(4, '0')}`
}

export function jobPartNumber(job: { domains?: string[] | null; part_seq?: number | null }) {
  return formatPartNumber(primaryDomainCode(job) ?? 'SYS', job.part_seq)
}

/** Matches `KRN-0141`, `KRN·0141`, `krn 141`, or a bare `0141`. */
export function parsePartNumberQuery(query: string) {
  const match = /^\s*([a-z]{3})?[\s·.\-_]*(\d{1,6})\s*$/i.exec(query)
  if (!match) return null
  const code = match[1] ? match[1].toUpperCase() : null
  if (code && !DOMAIN_BY_CODE.has(code as DomainCode)) return null
  return { code: code as DomainCode | null, seq: Number(match[2]) }
}

// ---------------------------------------------------------------------------
// Pixel glyphs (appendix A §A4)
//
// Source format is a 16×16 character grid, which is the authoring format on
// purpose: it is diffable, and the low resolution is a constraint rather than a
// style. `#` paints in the current text colour, `o` paints in the accent.
// Rendered as <rect> arrays so any display size stays sharp and both themes
// follow `currentColor`.
// ---------------------------------------------------------------------------

export const GLYPH_GRIDS: Record<DomainCode, readonly string[]> = {
  // Chip body with pins
  KRN: [
    '................',
    '................',
    '...#..#..#..#...',
    '...#..#..#..#...',
    '..############..',
    '..#..........#..',
    '#.#..oooooo..#.#',
    '#.#..oooooo..#.#',
    '#.#..oooooo..#.#',
    '#.#..oooooo..#.#',
    '..#..........#..',
    '..############..',
    '...#..#..#..#...',
    '...#..#..#..#...',
    '................',
    '................',
  ],
  // Funnel: many inputs, one output
  CMP: [
    '................',
    '..############..',
    '..############..',
    '...##########...',
    '....########....',
    '.....######.....',
    '......####......',
    '......oooo......',
    '......oooo......',
    '......oooo......',
    '.....oooooo.....',
    '....oooooooo....',
    '................',
    '................',
    '................',
    '................',
  ],
  // Database cylinder
  DBS: [
    '................',
    '...##########...',
    '..############..',
    '...##########...',
    '..#..........#..',
    '..#..........#..',
    '..#.oooooooo.#..',
    '..#..........#..',
    '..#..........#..',
    '..#.oooooooo.#..',
    '..#..........#..',
    '..#..........#..',
    '...##########...',
    '....########....',
    '................',
    '................',
  ],
  // Platter and spindle
  STO: [
    '................',
    '.....######.....',
    '...##########...',
    '..############..',
    '.####......####.',
    '.###........###.',
    '.###...oo...###.',
    '.###..oooo..###.',
    '.###..oooo..###.',
    '.###...oo...###.',
    '.###........###.',
    '.####......####.',
    '..############..',
    '...##########...',
    '.....######.....',
    '................',
  ],
  // Centre node, four corner nodes
  NET: [
    '................',
    '.##.........##..',
    '.##.........##..',
    '...#.......#....',
    '....#.....#.....',
    '.....#...#......',
    '......ooo.......',
    '......ooo.......',
    '.....#...#......',
    '....#.....#.....',
    '...#.......#....',
    '.##.........##..',
    '.##.........##..',
    '................',
    '................',
    '................',
  ],
  // Four guests inside one host
  VRT: [
    '................',
    '.##############.',
    '.#............#.',
    '.#.oooo..oooo.#.',
    '.#.oooo..oooo.#.',
    '.#.oooo..oooo.#.',
    '.#............#.',
    '.#.oooo..oooo.#.',
    '.#.oooo..oooo.#.',
    '.#.oooo..oooo.#.',
    '.#............#.',
    '.##############.',
    '................',
    '................',
    '................',
    '................',
  ],
  // Dev board with a pin header
  EMB: [
    '................',
    '.##############.',
    '.#............#.',
    '.#..oooooo....#.',
    '.#..oooooo....#.',
    '.#..oooooo....#.',
    '.#............#.',
    '.#.#.#.#.#.#..#.',
    '.#.#.#.#.#.#..#.',
    '.#............#.',
    '.##############.',
    '................',
    '................',
    '................',
    '................',
    '................',
  ],
  // Flame graph
  PRF: [
    '................',
    '......###.......',
    '......###.......',
    '....ooooooo.....',
    '....ooooooo.....',
    '..#########.....',
    '..#########.....',
    '..ooooooooooo...',
    '..ooooooooooo...',
    '.#############..',
    '.#############..',
    '.##############.',
    '.##############.',
    '................',
    '................',
    '................',
  ],
}

export type GlyphRect = { x: number; y: number; accent: boolean }

export function glyphRects(code: string | null | undefined): GlyphRect[] {
  const grid = GLYPH_GRIDS[String(code || '').toUpperCase() as DomainCode]
  if (!grid) return []
  const rects: GlyphRect[] = []
  for (let y = 0; y < grid.length; y += 1) {
    const row = grid[y]
    for (let x = 0; x < row.length; x += 1) {
      const cell = row[x]
      if (cell === '.') continue
      rects.push({ x, y, accent: cell === 'o' })
    }
  }
  return rects
}

/** String renderer for server-rendered HTML and the ops console. */
export function glyphSvg(code: string | null | undefined, size: number) {
  const rects = glyphRects(code)
  if (!rects.length) return ''
  const body = rects
    .map((rect) => `<rect x="${rect.x}" y="${rect.y}" width="1" height="1" fill="${rect.accent ? 'var(--anodize)' : 'currentColor'}"/>`)
    .join('')
  return `<svg class="glyph" width="${size}" height="${size}" viewBox="0 0 16 16" shape-rendering="crispEdges" aria-hidden="true" focusable="false">${body}</svg>`
}
